import { type AnyIcon, Icon } from '@components/icon/Icon';
import cx from 'classnames';
import { Tooltip } from '@shared/components/tooltip/Tooltip';
import styles from './IconToggleButton.scss';

export type IconToggleButtonProps = {
  title: string;
  checked: boolean;
  iconName: AnyIcon;
  onClick: () => void;
  /** With `dataTooltipId`, wraps the button in Tooltip (Show/Hide prefixes). */
  tooltipContent?: string;
  /** Built-in tooltip: required with `tooltipContent`. External tooltip: optional alone. */
  dataTooltipId?: string;
  place?: 'top' | 'bottom' | 'left' | 'right';
  /** When set, this exact string is used for both the Tooltip content and the
   * button's aria-label, bypassing the auto "Show "/"Hide " prefix. */
  tooltipContentOverride?: string;
  iconClassName?: string;
};

export const IconToggleButton = (props: IconToggleButtonProps) => {
  const { title, checked, iconName, onClick, iconClassName } = props;
  const builtInTooltip =
    typeof props.tooltipContent === 'string' && props.dataTooltipId
      ? {
          content: props.tooltipContent,
          id: props.dataTooltipId,
        }
      : null;
  const ariaLabel =
    props.tooltipContentOverride ??
    (checked ? `Hide ${title}` : `Show ${title}`);

  const button = (
    <button
      role="switch"
      type="button"
      aria-checked={checked}
      aria-label={ariaLabel}
      data-tooltip-id={builtInTooltip ? undefined : props.dataTooltipId}
      onClick={onClick}
      title={builtInTooltip || props.dataTooltipId ? undefined : title}
    >
      <Icon
        name={iconName}
        ariaLabel={ariaLabel}
        className={cx({
          [styles.active]: checked,
          [`${iconClassName}`]: !!iconClassName,
        })}
      />
    </button>
  );

  const content = builtInTooltip ? (
    <Tooltip
      content={
        props.tooltipContentOverride ??
        (checked
          ? `Hide ${builtInTooltip.content}`
          : `Show ${builtInTooltip.content}`)
      }
      id={builtInTooltip.id}
      place={props.place ?? 'top'}
      tooltipClassName={styles.iconTooltip}
    >
      {button}
    </Tooltip>
  ) : (
    button
  );

  return <div className={styles.container}>{content}</div>;
};
